import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { allLocalFlashcards, cardAudio, type LearningCard } from '@/content/mobile';
import { localResources } from '@/content/resources';
import { collapseSplitResources } from '@/lib/hub-sections';
import { track } from '@/lib/analytics';
import { mergeWebResources } from '@/lib/learner-catalog';
import { learnerApiJson } from '@/lib/learner-api';
import { playAudio } from '@/lib/play-audio';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';
import { typeScale } from '@/lib/typography';

export const Route = createFileRoute('/app/search')({ component: SearchPage });

function SearchPage() {
  const [query, setQuery] = useState('');
  const auth = useLearnerAuth();
  const { dialect, baseLanguage, voice } = useLearnerPreferences();
  const cards = useMemo(() => allLocalFlashcards(dialect, baseLanguage), [baseLanguage, dialect]);
  const cloud = useQuery({
    queryKey: ['search-catalogs', dialect],
    queryFn: async () => {
      const mode = dialect === 'moob' ? 'moob' : 'hmonglus';
      const [flashcards, sheets, stories] = await Promise.all([
        learnerApiJson<Array<{ slug: string; name: string; title_translations: Record<string, string>; requires_subscription?: boolean }>>(auth?.getToken ?? null, `/v1/flashcard-sets?mode=${mode}`),
        learnerApiJson<Array<{ slug: string; name: string; title_translations: Record<string, string>; requires_subscription?: boolean }>>(auth?.getToken ?? null, `/v1/term-sheets?mode=${mode}`),
        learnerApiJson<Array<{ slug: string; name: string; title_translations: Record<string, string>; requires_subscription?: boolean }>>(auth?.getToken ?? null, `/v1/storybooks?mode=${mode}`),
      ]);
      return [
        ...flashcards.map((item) => ({ slug: item.slug, title: item.title_translations.en || item.name, category: 'Flashcards' as const, href: `/app/flashcards/${item.slug}`, premium: Boolean(item.requires_subscription) })),
        ...sheets.map((item) => ({ slug: item.slug, title: item.title_translations.en || item.name, category: 'Grammar' as const, href: `/app/sheets/${item.slug}`, premium: Boolean(item.requires_subscription) })),
        ...stories.map((item) => ({ slug: item.slug, title: item.title_translations.en || item.name, category: 'Storybooks' as const, href: `/app/storybooks/${item.slug}`, premium: Boolean(item.requires_subscription) })),
      ];
    },
    retry: 1,
  });
  const catalog = useMemo(
    () => collapseSplitResources(mergeWebResources(localResources, cloud.data)),
    [cloud.data],
  );
  const normalized = query.trim().toLocaleLowerCase();
  const resourceMatches = useMemo(() => {
    if (!normalized) return [];
    return catalog
      .filter((resource) => `${resource.title} ${resource.category}`.toLocaleLowerCase().includes(normalized))
      .slice(0, 5);
  }, [catalog, normalized]);
  const termMatches = useMemo(() => {
    if (!normalized) return [];
    const exact: LearningCard[] = [];
    const partial: LearningCard[] = [];
    for (const card of cards) {
      const title = card.title.toLocaleLowerCase();
      const translation = card.translation.toLocaleLowerCase();
      if (!title.includes(normalized) && !translation.includes(normalized)) continue;
      (title === normalized || translation === normalized ? exact : partial).push(card);
    }
    return [...exact, ...partial].slice(0, 200);
  }, [cards, normalized]);

  return (
    <div className="mx-auto max-w-5xl px-5 py-8 sm:px-8 lg:py-12">
      <h1 className="sr-only">Search</h1>
      <label className="block">
        <span className="sr-only">Search Hmonglus keywords and phrases</span>
        <input
          autoFocus
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && track('Search Submitted', { query_length: query.length })}
          placeholder={`Search ${dialect === 'moob' ? 'Moob' : 'Hmonglus'} keywords and phrases`}
          className={`w-full rounded-2xl border border-[#b7dfb0] bg-white px-5 py-4 outline-none focus:border-[#399918] focus:ring-4 focus:ring-[#399918]/15 ${typeScale.searchInput}`}
        />
      </label>
      {!normalized ? (
        <p className="mt-8 text-center text-slate-500">Search Hmong words, translations, and lessons.</p>
      ) : (
        <>
          <p className="mt-4 text-sm text-slate-500">
            {resourceMatches.length + termMatches.length} results
          </p>
          <div className="mt-5 space-y-3">
            {resourceMatches.map((resource) => (
              <a
                key={resource.href}
                href={resource.href}
                onClick={() => track('Search Result Press', { resource: resource.slug })}
                className="flex items-center justify-between rounded-2xl border border-[#cfe8c6] bg-white p-5 hover:border-[#399918]"
              >
                <span>
                  <span className={`${typeScale.cardCategory} text-[#399918]`}>{resource.category}</span>
                  <h2 className={`mt-1 ${typeScale.searchResultTitle}`}>{resource.title}</h2>
                </span>
                {resource.premium ? <span className="text-xs font-bold text-[#8a5b00]">Premium</span> : null}
              </a>
            ))}
            {termMatches.map((card) => (
              <div key={card.persistencyKey} className="flex items-center justify-between rounded-2xl border border-[#cfe8c6] bg-white p-5">
                <div>
                  <p className="font-nunito text-xl font-bold">{highlight(card.title, query)}</p>
                  <p className="font-nunito text-base italic text-slate-500">{highlight(card.translation, query)}</p>
                </div>
                {cardAudio(card, voice) ? (
                  <button
                    type="button"
                    aria-label={`Hear ${card.title}`}
                    onClick={() => void playAudio(cardAudio(card, voice))}
                    className="grid h-11 w-11 place-items-center rounded-full bg-[#ECFFE6] font-black text-[#2d7a13]"
                  >
                    ▶
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function highlight(text: string, query: string) {
  const index = text.toLocaleLowerCase().indexOf(query.trim().toLocaleLowerCase());
  if (index < 0 || !query.trim()) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-[#fff0a8]">{text.slice(index, index + query.trim().length)}</mark>
      {text.slice(index + query.trim().length)}
    </>
  );
}
